import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import {
  UsersIcon,
  ShoppingBagIcon,
  CalendarIcon,
  CreditCardIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import { dashboardAPI } from '../../utils/api';
import { dashboardRefreshEmitter } from '../../utils/dashboardRefresh';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrders: 0,
    totalBookings: 0,
    totalRevenue: 0,
    todayRevenue: 0,
    ordersToday: 0,
    bookingsToday: 0,
    ordersPending: 0,
    ordersCompleted: 0,
    bookingsPending: 0,
    bookingsConfirmed: 0,
    currentMonth: '',
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [topItems, setTopItems] = useState([]);

  useEffect(() => {
    // Check if there's a pending refresh from another page
    const pendingRefresh = localStorage.getItem('dashboardRefreshFlag');
    if (pendingRefresh) {
      console.log('Pending refresh detected, fetching dashboard data');
      fetchDashboardData();
      localStorage.removeItem('dashboardRefreshFlag');
    } else {
      fetchDashboardData();
    }
    
    // Subscribe to dashboard refresh events
    const unsubscribe = dashboardRefreshEmitter.subscribe(() => {
      console.log('Dashboard refresh event received');
      fetchDashboardData(true);
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  const fetchDashboardData = async (isAutoRefresh = false) => {
    try {
      if (isAutoRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Fetch dashboard statistics from the API
      const dashboardResponse = await dashboardAPI.getStats();

      // API returns { success: true, data: { totalOrders, totalRevenue, recentOrders, ... } }
      if (dashboardResponse.data.success === true) {
        const data = dashboardResponse.data.data;
        
        setStats({
          totalUsers: data.totalUsers || 0,
          totalOrders: data.totalOrders || 0,
          totalBookings: data.totalBookings || 0,
          totalRevenue: parseFloat(data.totalRevenue) || 0,
          todayRevenue: parseFloat(data.todayRevenue) || 0,
          ordersToday: data.ordersToday || 0,
          bookingsToday: data.bookingsToday || 0,
          ordersPending: data.ordersPending || 0,
          ordersCompleted: data.ordersCompleted || 0,
          bookingsPending: data.bookingsPending || 0,
          bookingsConfirmed: data.bookingsConfirmed || 0,
          currentMonth: data.currentMonth || '',
        });

        setRecentOrders(data.recentOrders || []);
        setRecentBookings(data.recentBookings || []);
        setTopItems(data.topItems || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response) {
        console.error('Response error:', error.response.data);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const statCards = [
    {
      name: 'Total Orders',
      value: stats.totalOrders,
      change: `+${stats.ordersToday} today`,
      icon: ShoppingBagIcon,
      bgColor: 'bg-blue-500',
      link: '/admin/orders',
    },
    {
      name: 'Total Bookings',
      value: stats.totalBookings,
      change: `+${stats.bookingsToday} today`,
      icon: CalendarIcon,
      bgColor: 'bg-green-500',
      link: '/admin/bookings',
    },
    {
      name: 'Today\'s Revenue',
      value: `₱${stats.todayRevenue.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`,
      change: 'Today',
      icon: CreditCardIcon,
      bgColor: 'bg-amber-500',
      link: '/admin/payments',
    },
    {
      name: 'Total Users',
      value: stats.totalUsers,
      change: 'Registered',
      icon: UsersIcon,
      bgColor: 'bg-purple-500',
      link: '/admin/users',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-5 lg:space-y-6 w-full overflow-hidden px-3 sm:px-0">
      {/* Welcome Message */}
      <div className="bg-white shadow rounded-lg p-3 sm:p-5 lg:p-6">
        <h2 className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 break-words">
          Welcome back, {user?.full_name}!
        </h2>
        <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600">
          Here's what's happening with your restaurant today.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 lg:gap-5">
        {statCards.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className="bg-white shadow rounded-lg hover:shadow-lg transition-shadow active:scale-95"
          >
            <div className="p-2.5 sm:p-5 lg:p-6">
              <div className="flex flex-col gap-1.5 sm:gap-3">
                <div className={`flex-shrink-0 ${stat.bgColor} rounded-md p-1.5 sm:p-3 w-fit`}>
                  <stat.icon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-white" />
                </div>
                <div className="overflow-hidden min-w-0">
                  <dt className="text-[10px] sm:text-sm font-medium text-gray-500 uppercase tracking-tight truncate">{stat.name}</dt>
                  <dd className="mt-0.5 sm:mt-2">
                    <div className="text-sm sm:text-xl lg:text-2xl font-bold text-gray-900 break-words leading-tight">{stat.value}</div>
                  </dd>
                  <dd className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">{stat.change}</dd>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-2 gap-2.5 sm:gap-4 lg:grid-cols-4">
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Pending Orders</div>
          <div className="mt-1.5 sm:mt-2 text-lg sm:text-2xl lg:text-3xl font-semibold text-yellow-600">{stats.ordersPending}</div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Completed Orders</div>
          <div className="mt-1.5 sm:mt-2 text-lg sm:text-2xl lg:text-3xl font-semibold text-green-600">{stats.ordersCompleted}</div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Pending Bookings</div>
          <div className="mt-1.5 sm:mt-2 text-lg sm:text-2xl lg:text-3xl font-semibold text-yellow-600">{stats.bookingsPending}</div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Confirmed Bookings</div>
          <div className="mt-1.5 sm:mt-2 text-lg sm:text-2xl lg:text-3xl font-semibold text-green-600">{stats.bookingsConfirmed}</div>
        </div>
      </div>

      {/* Recent Orders and Bookings */}
      <div className="grid grid-cols-1 gap-3 sm:gap-5 xl:grid-cols-2">
        {/* Recent Orders */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="px-3 py-3 sm:px-6 sm:py-5 border-b border-gray-200">
            <h3 className="text-sm sm:text-lg font-medium leading-6 text-gray-900">Recent Orders</h3>
          </div>
          <div className="px-3 py-3 sm:p-6">
            {recentOrders.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No orders yet</p>
            ) : (
              <div className="space-y-4">
                {recentOrders.map((order) => (
                  <div
                    key={order.order_id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-4 last:pb-0 border-b last:border-b-0 border-gray-100"
                  >
                    <div className="mb-2 sm:mb-0 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{order.transaction_id}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {order.order_date}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 sm:flex-col sm:items-end sm:gap-1.5">
                      <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        ₱{parseFloat(order.total_amount).toLocaleString('en-PH')}
                      </p>
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          order.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {order.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-5">
              <Link
                to="/admin/orders"
                className="text-sm font-medium text-amber-600 hover:text-amber-500 inline-flex items-center"
              >
                View all orders →
              </Link>
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="px-3 py-3 sm:px-6 sm:py-5 border-b border-gray-200">
            <h3 className="text-sm sm:text-lg font-medium leading-6 text-gray-900">Recent Bookings</h3>
          </div>
          <div className="px-3 py-3 sm:p-6">
            {recentBookings.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No bookings yet</p>
            ) : (
              <div className="space-y-4">
                {recentBookings.map((booking) => (
                  <div
                    key={booking.booking_id}
                    className="flex flex-col sm:flex-row sm:items-start sm:justify-between pb-4 last:pb-0 border-b last:border-b-0 border-gray-100"
                  >
                    <div className="mb-3 sm:mb-0 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {booking.user_name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(booking.booking_date).toLocaleDateString('en-PH')} • {booking.booking_time}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{booking.guest_count} guests</p>
                    </div>
                    <div className="flex items-center gap-3 sm:flex-col sm:items-end sm:gap-1.5">
                      <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        ₱{parseFloat(booking.total_amount || 0).toLocaleString('en-PH')}
                      </p>
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'confirmed'
                            ? 'bg-green-100 text-green-800'
                            : booking.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : booking.status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {booking.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-5">
              <Link
                to="/admin/bookings"
                className="text-sm font-medium text-amber-600 hover:text-amber-500 inline-flex items-center"
              >
                View all bookings →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Top Selling Items */}
      {topItems.length > 0 && (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="px-3 py-3 sm:px-6 sm:py-5 border-b border-gray-200">
            <h3 className="text-sm sm:text-lg font-medium leading-6 text-gray-900">Top Selling Items (Last 30 Days)</h3>
          </div>
          <div className="overflow-x-auto">
            <div className="px-3 py-3 sm:p-6">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qty Sold
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {topItems.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 sm:px-6 py-4 text-sm font-medium text-gray-900 max-w-[200px] truncate">
                        {item.name}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {item.total_quantity}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₱{parseFloat(item.total_revenue).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg p-3 sm:p-6">
        <h3 className="text-sm sm:text-lg font-medium text-gray-900 mb-3 sm:mb-5">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-2.5 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
          <Link
            to="/admin/menu"
            className="flex flex-col items-center justify-center p-3 sm:p-5 bg-gray-50 rounded-lg hover:bg-gray-100 active:scale-95 transition-all"
          >
            <ShoppingBagIcon className="h-7 w-7 sm:h-10 sm:w-10 text-amber-600 mb-1.5 sm:mb-3" />
            <span className="text-[10px] sm:text-sm font-medium text-gray-900 text-center leading-tight">Manage Menu</span>
          </Link>
          <Link
            to="/admin/orders"
            className="flex flex-col items-center justify-center p-3 sm:p-5 bg-gray-50 rounded-lg hover:bg-gray-100 active:scale-95 transition-all"
          >
            <ShoppingBagIcon className="h-7 w-7 sm:h-10 sm:w-10 text-blue-600 mb-1.5 sm:mb-3" />
            <span className="text-[10px] sm:text-sm font-medium text-gray-900 text-center leading-tight">View Orders</span>
          </Link>
          <Link
            to="/admin/bookings"
            className="flex flex-col items-center justify-center p-3 sm:p-5 bg-gray-50 rounded-lg hover:bg-gray-100 active:scale-95 transition-all col-span-2 md:col-span-1"
          >
            <CalendarIcon className="h-7 w-7 sm:h-10 sm:w-10 text-green-600 mb-1.5 sm:mb-3" />
            <span className="text-[10px] sm:text-sm font-medium text-gray-900 text-center leading-tight">Manage Bookings</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
